import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Loader2, FileText, Building2, QrCode, ShieldCheck, CreditCard, Copy, Clock, CheckCircle2, XCircle, Smartphone, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";

function copyToClipboard(text, label) {
  if (!text) return;
  navigator.clipboard?.writeText(String(text));
  toast.success(`${label || "Copied"} copied`);
}

function genTempRef() {
  return "DV-MAN-" + Math.random().toString(36).slice(2, 12).toUpperCase();
}

function buildUpiIntent({ pa, pn, am, tn }) {
  const params = new URLSearchParams();
  if (pa) params.set("pa", pa);
  if (pn) params.set("pn", pn);
  if (am) params.set("am", String(am));
  if (tn) params.set("tn", tn);
  params.set("cu", "INR");
  return `upi://pay?${params.toString()}`;
}

function ManualPaymentPanel({ plan, cycle, amount, coupon, manual, onSubmitted }) {
  const [method, setMethod] = useState(manual?.upi_id ? "upi" : "bank");
  const [utr, setUtr] = useState("");
  const [payerName, setPayerName] = useState("");
  const [payerReference, setPayerReference] = useState("");
  const [notes, setNotes] = useState("");
  const [screenshot, setScreenshot] = useState("");
  const [busy, setBusy] = useState(false);
  // Client-side reference used only for the UPI intent `tn` field + display.
  // The authoritative server order_ref is returned on submit.
  const [tempRef] = useState(() => genTempRef());

  const upiIntent = useMemo(() => buildUpiIntent({
    pa: manual?.upi_id || "",
    pn: manual?.upi_display_name || "DraftVoice AI",
    am: amount,
    tn: tempRef,
  }), [manual, amount, tempRef]);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) { toast.error("Screenshot too large (max 2 MB)"); return; }
    const reader = new FileReader();
    reader.onload = () => setScreenshot(reader.result);
    reader.readAsDataURL(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!utr.trim() || !payerName.trim()) { toast.error("Please add UTR and payer name."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/payments/manual/request", {
        plan: plan.id, billing_cycle: cycle, method,
        utr: utr.trim(), payer_name: payerName.trim(),
        payer_reference: payerReference.trim(), screenshot_url: screenshot,
        notes: (notes.trim() ? notes.trim() + "\n" : "") + `Intent ref: ${tempRef}`,
        coupon_code: coupon || undefined,
      });
      toast.success(`Payment submitted — order ${data.order_ref}. We'll verify shortly.`);
      onSubmitted?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit payment");
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-5 rounded-3xl border border-brand-borderc bg-white p-5 lg:p-7" data-testid="manual-payment-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-brand-purple font-semibold">Manual UPI / Bank Transfer</div>
          <div className="mt-1 text-xl font-bold text-brand-navy">{plan.name} · ₹{amount.toLocaleString()} <span className="text-sm font-medium text-brand-muted">/ {cycle === "yearly" ? "yr" : "mo"}</span></div>
        </div>
        <div className="text-xs px-3 py-1.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 inline-flex items-center gap-1.5">
          <ShieldCheck className="w-3.5 h-3.5" /> Plan activates after admin verification
        </div>
      </div>

      <div className="mt-5 grid lg:grid-cols-2 gap-5">
        {/* Left: payment details */}
        <div className="space-y-4">
          {manual?.upi_id && (
            <div className={`rounded-2xl border p-4 ${method === "upi" ? "border-brand-purple bg-purple-50/40" : "border-brand-borderc"}`} onClick={() => setMethod("upi")}>
              <label className="flex items-center gap-2 font-semibold text-brand-navy cursor-pointer"><input type="radio" checked={method === "upi"} onChange={() => setMethod("upi")} /> <QrCode className="w-4 h-4" /> UPI QR / UPI ID</label>

              {/* Quick summary chips: amount + order ref + UPI id, each with copy */}
              <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
                <div className="rounded-xl border border-brand-borderc bg-white px-3 py-2">
                  <div className="text-brand-muted">Amount</div>
                  <div className="font-bold text-brand-navy flex items-center gap-1.5">₹{amount.toLocaleString()}
                    <button data-testid="copy-amount" type="button" onClick={(e) => { e.stopPropagation(); copyToClipboard(amount, "Amount"); }} className="text-brand-purple"><Copy className="w-3 h-3" /></button>
                  </div>
                </div>
                <div className="rounded-xl border border-brand-borderc bg-white px-3 py-2">
                  <div className="text-brand-muted">Order ref</div>
                  <div className="font-mono text-brand-navy text-[11px] flex items-center gap-1.5" data-testid="order-ref-value">{tempRef}
                    <button data-testid="copy-orderref" type="button" onClick={(e) => { e.stopPropagation(); copyToClipboard(tempRef, "Order reference"); }} className="text-brand-purple"><Copy className="w-3 h-3" /></button>
                  </div>
                </div>
                <div className="rounded-xl border border-brand-borderc bg-white px-3 py-2 col-span-1">
                  <div className="text-brand-muted">UPI ID</div>
                  <div className="font-mono text-brand-navy text-[11px] flex items-center gap-1.5 truncate" data-testid="upi-id-value">{manual.upi_id}
                    <button data-testid="copy-upi" type="button" onClick={(e) => { e.stopPropagation(); copyToClipboard(manual.upi_id, "UPI ID"); }} className="text-brand-purple shrink-0"><Copy className="w-3 h-3" /></button>
                  </div>
                </div>
              </div>

              <div className="mt-3 flex items-start gap-4">
                {manual.upi_qr_url ? (
                  <img src={manual.upi_qr_url} alt="UPI QR" className="w-32 h-32 rounded-xl border border-brand-borderc bg-white object-contain" data-testid="upi-qr-img" />
                ) : (
                  <div className="w-32 h-32 rounded-xl border border-dashed border-brand-borderc flex items-center justify-center text-xs text-brand-muted text-center px-2">No QR uploaded. Use the UPI ID above or the button below.</div>
                )}
                <div className="text-sm space-y-2 flex-1">
                  <div>
                    <div className="text-brand-muted text-xs">Pay to</div>
                    <div className="font-semibold text-brand-navy">{manual.upi_display_name || "DraftVoice AI"}</div>
                  </div>
                  <a
                    data-testid="upi-intent-btn"
                    href={upiIntent}
                    onClick={(e) => e.stopPropagation()}
                    className="inline-flex items-center justify-center gap-2 w-full btn-primary-gradient rounded-full py-2.5 text-sm font-semibold"
                  >
                    <Smartphone className="w-4 h-4" /> Pay with UPI App
                  </a>
                  <div className="text-[11px] text-brand-muted">
                    On mobile: opens GPay / PhonePe / Paytm / BHIM with the amount pre-filled.<br />
                    On desktop: scan the QR or copy the UPI ID above.
                  </div>
                </div>
              </div>

              <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-800 flex gap-2">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                <div>After payment, enter your <strong>UTR / transaction ID</strong> below. Your plan activates only after admin verification — UTR or screenshot alone is <strong>not</strong> auto-approved.</div>
              </div>
            </div>
          )}

          {manual?.bank_account_number && manual?.bank_ifsc && (
            <div className={`rounded-2xl border p-4 cursor-pointer ${method === "bank" ? "border-brand-purple bg-purple-50/40" : "border-brand-borderc"}`} onClick={() => setMethod("bank")}>
              <label className="flex items-center gap-2 font-semibold text-brand-navy"><input type="radio" checked={method === "bank"} onChange={() => setMethod("bank")} /> <Building2 className="w-4 h-4" /> Bank Transfer (NEFT / IMPS)</label>
              <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                <div><div className="text-brand-muted text-xs">Account holder</div><div className="font-semibold text-brand-navy">{manual.bank_account_name}</div></div>
                <div><div className="text-brand-muted text-xs">Bank</div><div className="font-semibold text-brand-navy">{manual.bank_name}</div></div>
                <div><div className="text-brand-muted text-xs">Account number</div><div className="font-mono text-brand-navy flex items-center gap-1.5">{manual.bank_account_number}<button type="button" onClick={() => copyToClipboard(manual.bank_account_number)} className="text-brand-purple"><Copy className="w-3 h-3" /></button></div></div>
                <div><div className="text-brand-muted text-xs">IFSC</div><div className="font-mono text-brand-navy flex items-center gap-1.5">{manual.bank_ifsc}<button type="button" onClick={() => copyToClipboard(manual.bank_ifsc)} className="text-brand-purple"><Copy className="w-3 h-3" /></button></div></div>
                {manual.bank_branch && <div className="col-span-2"><div className="text-brand-muted text-xs">Branch</div><div className="text-brand-navy">{manual.bank_branch}</div></div>}
              </div>
            </div>
          )}

          {manual?.instructions && (
            <div className="rounded-2xl border border-brand-borderc bg-brand-surface p-4 text-sm text-brand-text whitespace-pre-line">{manual.instructions}</div>
          )}
          {(manual?.support_email || manual?.support_whatsapp) && (
            <div className="text-xs text-brand-muted">
              Need help? {manual.support_email && <a href={`mailto:${manual.support_email}`} className="text-brand-purple hover:underline">{manual.support_email}</a>}
              {manual.support_whatsapp && <> · WhatsApp <a href={`https://wa.me/${manual.support_whatsapp.replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer" className="text-brand-purple hover:underline">{manual.support_whatsapp}</a></>}
            </div>
          )}
        </div>

        {/* Right: confirmation form */}
        <form onSubmit={submit} className="rounded-2xl border border-brand-borderc bg-brand-surface p-5 space-y-3">
          <div className="font-semibold text-brand-navy">Confirm your payment</div>
          <p className="text-xs text-brand-muted">After you send the money, submit the details below. Your plan activates once admin verifies the payment — usually within a few hours. <strong className="text-brand-navy">UTR and screenshots are never auto-approved.</strong></p>
          <div>
            <label className="text-xs text-brand-muted">UTR / Transaction ID</label>
            <input data-testid="manual-utr" required value={utr} onChange={(e) => setUtr(e.target.value)} placeholder="e.g. 408712345678" className="w-full px-3 py-2.5 rounded-xl border border-brand-borderc text-sm bg-white" />
          </div>
          <div>
            <label className="text-xs text-brand-muted">Payer name</label>
            <input data-testid="manual-payer-name" required value={payerName} onChange={(e) => setPayerName(e.target.value)} placeholder="Name on UPI / bank account" className="w-full px-3 py-2.5 rounded-xl border border-brand-borderc text-sm bg-white" />
          </div>
          <div>
            <label className="text-xs text-brand-muted">{method === "upi" ? "Payer UPI ID (optional)" : "Bank reference (optional)"}</label>
            <input data-testid="manual-payer-ref" value={payerReference} onChange={(e) => setPayerReference(e.target.value)} placeholder={method === "upi" ? "yourname@upi" : "Reference / remarks"} className="w-full px-3 py-2.5 rounded-xl border border-brand-borderc text-sm bg-white" />
          </div>
          <div>
            <label className="text-xs text-brand-muted">Payment screenshot (optional)</label>
            <input data-testid="manual-screenshot" type="file" accept="image/*" onChange={onFile} className="block w-full text-xs mt-1" />
            {screenshot && <div className="mt-2 text-xs text-emerald-600">Screenshot attached ({Math.round(screenshot.length / 1024)} KB)</div>}
          </div>
          <div>
            <label className="text-xs text-brand-muted">Notes (optional)</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="w-full px-3 py-2 rounded-xl border border-brand-borderc text-sm bg-white" placeholder="Any context for our team" />
          </div>
          <button data-testid="manual-submit" disabled={busy} className="w-full btn-primary-gradient rounded-full py-2.5 font-semibold inline-flex items-center justify-center gap-2">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Submit for verification
          </button>
          <p className="text-[11px] text-brand-muted">By submitting you confirm the payment was sent. False claims may lead to account suspension.</p>
        </form>
      </div>
    </div>
  );
}

export default function BillingPage() {
  const { user, refresh } = useAuth();
  const [plans, setPlans] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [manual, setManual] = useState({ enabled: false });
  const [methods, setMethods] = useState([]);
  const [myRequests, setMyRequests] = useState([]);
  const [cycle, setCycle] = useState("monthly");
  const [coupon, setCoupon] = useState("");
  const [busy, setBusy] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      const [p, i, mp, pm, my] = await Promise.all([
        api.get("/plans"),
        api.get("/invoices"),
        api.get("/payments/manual/settings"),
        api.get("/payments/methods"),
        api.get("/payments/manual/my"),
      ]);
      setPlans(p.data);
      setInvoices(i.data);
      setManual(mp.data);
      setMethods(pm.data.methods || []);
      setMyRequests(my.data);
    })();
  }, []);

  const choosePlan = (p) => {
    setSelectedPlan(p);
    if (methods.length === 0) {
      toast.error("No payment method is configured. Please contact support.");
      return;
    }
    setSelectedMethod(methods[0].id);
    setTimeout(() => document.getElementById("checkout-panel")?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
  };

  const automatedCheckout = async () => {
    if (!selectedPlan) return;
    setBusy(selectedPlan.id);
    try {
      const provider = selectedMethod === "phonepe" ? "phonepe" : "razorpay";
      const { data } = await api.post("/subscription/checkout", { plan: selectedPlan.id, billing_cycle: cycle, provider, coupon_code: coupon || undefined });
      const verify = await api.post("/payments/verify", { order_id: data.order_id, payment_id: `pay_${Date.now()}`, signature: "mock" });
      if (verify.data.ok) {
        toast.success(`Upgraded to ${selectedPlan.name}!`);
        refresh();
        const r = await api.get("/invoices"); setInvoices(r.data);
        setSelectedPlan(null);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Payment failed");
    } finally { setBusy(null); }
  };

  const amount = useMemo(() => {
    if (!selectedPlan) return 0;
    return cycle === "yearly" ? selectedPlan.yearly_inr : selectedPlan.price_inr;
  }, [selectedPlan, cycle]);

  const onManualSubmitted = async (req) => {
    const my = await api.get("/payments/manual/my");
    setMyRequests(my.data);
    setSelectedPlan(null);
    refresh();
  };

  return (
    <div className="px-5 lg:px-10 py-8 max-w-6xl mx-auto" data-testid="billing-page">
      <h1 className="text-3xl font-bold text-brand-navy">Billing</h1>
      <p className="text-sm text-brand-muted mt-1">Current plan: <span className="font-semibold text-brand-navy capitalize">{user?.plan}</span></p>

      <div className="mt-6 flex items-center gap-2">
        <button data-testid="cycle-monthly" onClick={() => setCycle("monthly")} className={`px-4 py-1.5 rounded-full text-sm font-medium ${cycle === "monthly" ? "bg-gradient-primary text-white" : "bg-white border border-brand-borderc text-brand-text"}`}>Monthly</button>
        <button data-testid="cycle-yearly" onClick={() => setCycle("yearly")} className={`px-4 py-1.5 rounded-full text-sm font-medium ${cycle === "yearly" ? "bg-gradient-primary text-white" : "bg-white border border-brand-borderc text-brand-text"}`}>Yearly · save more</button>
      </div>

      <div className="mt-3 flex items-center gap-2">
        <input data-testid="billing-coupon" value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())} placeholder="Coupon code (try WELCOME20)" className="px-3 py-2 rounded-xl border border-brand-borderc text-sm focus:border-brand-purple outline-none" />
      </div>

      <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {plans.filter((p) => p.id !== "free").map((p) => {
          const price = cycle === "yearly" ? p.yearly_inr : p.price_inr;
          const isCurrent = user?.plan === p.id;
          const featured = p.popular;
          return (
            <div key={p.id} className={`relative rounded-3xl p-7 ${featured ? "bg-gradient-primary text-white" : "bg-white border border-brand-borderc"}`}>
              {featured && <span className="absolute top-5 right-5 text-[10px] uppercase tracking-widest font-bold bg-white/20 text-white px-2.5 py-1 rounded-full">Most popular</span>}
              <div className={`text-sm font-semibold ${featured ? "text-white/80" : "text-brand-muted"}`}>{p.name}</div>
              <div className="mt-2 flex items-baseline gap-1">
                <span className="text-4xl font-bold">₹{price.toLocaleString()}</span>
                <span className={`text-sm ${featured ? "text-white/80" : "text-brand-muted"}`}>/{cycle === "yearly" ? "yr" : "mo"}</span>
              </div>
              <ul className="mt-5 space-y-2 text-sm">
                {p.features.map((f) => (
                  <li key={f} className="flex gap-2"><Check className={`w-4 h-4 mt-0.5 ${featured ? "text-white" : "text-brand-purple"}`} />{f}</li>
                ))}
              </ul>
              <button
                data-testid={`billing-upgrade-${p.id}`}
                onClick={() => choosePlan(p)} disabled={isCurrent}
                className={`mt-6 w-full rounded-full py-3 font-semibold inline-flex items-center justify-center gap-2 ${
                  featured ? "bg-white text-brand-navy hover:bg-white/90" : "btn-primary-gradient"
                } ${isCurrent ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                {isCurrent ? "Current plan" : p.cta_label || "Upgrade"}
              </button>
            </div>
          );
        })}
      </div>

      {selectedPlan && (
        <div id="checkout-panel" className="mt-10 rounded-3xl bg-white border border-brand-borderc p-5 lg:p-7" data-testid="checkout-panel">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-xs uppercase tracking-widest text-brand-purple font-semibold">Checkout</div>
              <div className="text-2xl font-bold text-brand-navy">{selectedPlan.name} plan · ₹{amount.toLocaleString()}/{cycle === "yearly" ? "yr" : "mo"}</div>
              <div className="text-xs text-brand-muted mt-1">Order will be created for {user?.email}</div>
            </div>
            <button onClick={() => setSelectedPlan(null)} className="text-sm text-brand-muted hover:text-brand-navy">Change plan</button>
          </div>

          <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {methods.length === 0 && (
              <div className="col-span-full rounded-2xl border border-amber-200 bg-amber-50 text-amber-800 text-sm p-4">
                No payment methods are configured yet. Ask admin to enable Manual UPI/Bank or live Razorpay/PhonePe keys.
              </div>
            )}
            {methods.map((m) => (
              <button key={m.id} data-testid={`method-${m.id}`} onClick={() => setSelectedMethod(m.id)} className={`text-left rounded-2xl border p-4 transition ${selectedMethod === m.id ? "border-brand-purple ring-2 ring-purple-200" : "border-brand-borderc hover:border-brand-purple"}`}>
                <div className="flex items-center justify-between">
                  <div className="font-semibold text-brand-navy text-sm">{m.label}</div>
                  {m.recommended && <span className="text-[10px] font-bold uppercase tracking-widest bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full">Recommended</span>}
                </div>
                <div className="text-xs text-brand-muted mt-1">{m.manual ? "Manual — admin verifies" : "Automated · instant"}</div>
              </button>
            ))}
          </div>

          {selectedMethod && (selectedMethod === "upi" || selectedMethod === "bank") && manual.enabled && (
            <ManualPaymentPanel plan={selectedPlan} cycle={cycle} amount={amount} coupon={coupon} manual={manual} onSubmitted={onManualSubmitted} />
          )}

          {selectedMethod && (selectedMethod === "razorpay" || selectedMethod === "phonepe") && (
            <div className="mt-5 rounded-2xl border border-brand-borderc bg-white p-5 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3"><CreditCard className="w-5 h-5 text-brand-purple" /><div className="text-sm text-brand-text">You&apos;ll be redirected to {selectedMethod === "razorpay" ? "Razorpay" : "PhonePe"} for a secure checkout.</div></div>
              <button data-testid="automated-pay" disabled={busy === selectedPlan.id} onClick={automatedCheckout} className="btn-primary-gradient rounded-full px-5 py-2.5 font-semibold inline-flex items-center gap-2">
                {busy === selectedPlan.id && <Loader2 className="w-4 h-4 animate-spin" />} Pay ₹{amount.toLocaleString()}
              </button>
            </div>
          )}
        </div>
      )}

      {myRequests.length > 0 && (
        <div className="mt-10">
          <h2 className="text-xl font-semibold text-brand-navy">Your manual payment requests</h2>
          <div className="mt-3 bg-white rounded-2xl border border-brand-borderc divide-y divide-brand-borderc">
            {myRequests.map((r) => {
              const Icon = r.status === "approved" ? CheckCircle2 : r.status === "rejected" ? XCircle : Clock;
              const color = r.status === "approved" ? "text-emerald-600" : r.status === "rejected" ? "text-red-500" : "text-amber-600";
              return (
                <div key={r.id} className="px-5 py-3 text-sm" data-testid={`manual-req-${r.order_ref}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-semibold text-brand-navy">{r.plan.toUpperCase()} · {r.method.toUpperCase()} · ₹{r.amount}</div>
                      <div className="text-xs text-brand-muted">Ref {r.order_ref} · UTR {r.utr} · {new Date(r.created_at).toLocaleString()}</div>
                    </div>
                    <div className={`inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest ${color}`}><Icon className="w-4 h-4" />{r.status}</div>
                  </div>
                  {r.reject_reason && <div className="mt-1 text-xs text-red-600">Reason: {r.reject_reason}</div>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="mt-10">
        <h2 className="text-xl font-semibold text-brand-navy">Invoices</h2>
        {invoices.length === 0 ? (
          <div className="mt-3 text-center py-10 bg-white rounded-2xl border border-brand-borderc">
            <FileText className="w-10 h-10 mx-auto text-brand-purple" />
            <p className="text-sm text-brand-muted mt-2">No invoices yet.</p>
          </div>
        ) : (
          <div className="mt-3 bg-white rounded-2xl border border-brand-borderc divide-y divide-brand-borderc">
            {invoices.map((inv) => (
              <div key={inv.id} className="px-5 py-3 flex items-center justify-between text-sm">
                <div>
                  <div className="font-semibold text-brand-navy">{inv.plan.toUpperCase()} · {inv.provider}</div>
                  <div className="text-xs text-brand-muted">{new Date(inv.created_at).toLocaleString()}</div>
                </div>
                <div className="text-right">
                  <div className="font-bold text-brand-navy">₹{inv.amount}</div>
                  <div className="text-xs text-emerald-600">{inv.status}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
